import React, { useEffect } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { useState } from "react";
import { config } from "../../data/config";
import { getCookies } from "cookies-next";
import Swal from "sweetalert2";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
    ClassicEditor,
    Essentials,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    Heading,
    Paragraph,
    Link,
    List,
    Alignment,
    FontSize,
    FontFamily,
    FontColor,
    FontBackgroundColor,
    BlockQuote,
    Code,
    HorizontalLine,
    RemoveFormat,
    SourceEditing,
    Table,
    TableToolbar,
    TableProperties,
    TableCellProperties,
    Undo
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';

type LanguageCode = string;

// Helper to get multilingual value
const getMultilingualValue = (field: any, lang: LanguageCode, fallback: string = ''): string => {
    if (!field) return fallback;
    if (typeof field === 'string') return field; // Legacy support
    if (typeof field === 'object') {
        return field[lang] || field.en || field.fa || field.ku || field.ar || field.tr || fallback;
    }
    return fallback;
};

// Helper to set multilingual value
const setMultilingualValue = (currentData: any, lang: LanguageCode, value: string): any => {
    if (!currentData || typeof currentData !== 'object') {
        return { [lang]: value };
    }
    return {
        ...currentData,
        [lang]: value
    };
};

export default function QACreator({ parent, activeLang: propActiveLang }: { parent: any; activeLang?: LanguageCode }) {
    const { t } = useTranslation();
    let cookie = getCookies();

    // Language management
    const [languages, setLanguages] = useState<Array<{ code: string; label: string }>>([]);
    const [internalActiveLang, setInternalActiveLang] = useState<string>('fa'); // Internal state
    const activeLang = propActiveLang || internalActiveLang; // Use prop if provided, otherwise internal state

    const [data, setData] = React.useState<any>({
        parent: parent,
        title: {},
        description: {}
    });
    const [error, setError] = useState<any>(null);
    const [key, setKey] = useState<any>(0);
    const [list, setList] = useState<any>(null);
    const [create, SetCreate] = useState<any>(parent);
    const [isOpen, setIsOpen] = useState(false);
    const [translating, setTranslating] = useState(false);
    const editorRef = React.useRef<any>(null);
    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);

    // Supported languages
    const supportedLanguages = ['en', 'fa', 'ar', 'krd', 'tr'];
    const languageNames: Record<string, string> = {
        en: 'English',
        fa: 'فارسی',
        ar: 'العربية',
        krd: 'Kurdî',
        tr: 'Türkçe'
    };

    // Load languages
    const loadLanguages = async () => {
        try {
            const res = await axios.get(`${config.url}/v1/language`);
            const languageList = res.data?.message ?? [];
            const mapped = languageList
                .filter((lang: any) => lang.active)
                .sort((a: any, b: any) => (a.arranged ?? 0) - (b.arranged ?? 0))
                .map((lang: any) => ({ code: lang.code, label: lang.label }));
            setLanguages(mapped);
            // Set default to Persian if available, otherwise first language
            if (!propActiveLang) {
                const faLang = mapped.find((l: { code: string; label: string }) => l.code === 'fa');
                if (faLang) {
                    setInternalActiveLang('fa');
                } else if (mapped.length > 0) {
                    setInternalActiveLang(mapped[0].code);
                }
            }
        } catch (e) {
            console.error('Error loading languages:', e);
            const fallback = [
                { code: 'en', label: 'English' },
                { code: 'fa', label: 'فارسی' },
                { code: 'ar', label: 'العربية' },
                { code: 'krd', label: 'Kurdî' },
                { code: 'tr', label: 'Türkçe' }
            ];
            setLanguages(fallback);
            if (!propActiveLang) {
                setInternalActiveLang('fa');
            }
        }
    };

    const inputstyle = "py-3 px-5 block w-full bg-blue-100  rounded text-sm focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none";

    // Translation helper function - supports both plain text and HTML
    const translateText = async (text: string, fromLang: string, toLang: string, isHtml: boolean = false): Promise<string> => {
        if (!text || text.trim() === '') return '';

        try {
            // Try backend translation API first
            try {
                const response = await axios.post(
                    `${config.url}/v1/translate`,
                    {
                        text: text,
                        from: fromLang === 'krd' ? 'ku' : fromLang,
                        to: toLang === 'krd' ? 'ku' : toLang,
                        format: isHtml ? 'html' : 'text'
                    },
                    {
                        headers: { Authorization: 'Bearer ' + cookie['token'] },
                    }
                );
                if (response.data && response.data.translatedText) {
                    return response.data.translatedText;
                }
            } catch (backendError) {
                console.log('Backend translation not available, using client-side translation');
            }

            // For HTML content, extract text and translate while preserving structure
            if (isHtml && text.includes('<')) {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = text;
                
                // Extract text nodes and translate them
                const walker = document.createTreeWalker(
                    tempDiv,
                    NodeFilter.SHOW_TEXT,
                    {
                        acceptNode: (node) => {
                            const parent = node.parentElement;
                            if (parent && (parent.tagName === 'SCRIPT' || parent.tagName === 'STYLE')) {
                                return NodeFilter.FILTER_REJECT;
                            }
                            return NodeFilter.FILTER_ACCEPT;
                        }
                    }
                );

                const textNodes: Text[] = [];
                let node;
                while (node = walker.nextNode()) {
                    const text = node.textContent?.trim();
                    if (text && text.length > 0) {
                        textNodes.push(node as Text);
                    }
                }

                // Translate each text node
                for (const textNode of textNodes) {
                    try {
                        const originalText = textNode.textContent || '';
                        const response = await fetch(
                            `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLang === 'krd' ? 'ku' : fromLang}&tl=${toLang === 'krd' ? 'ku' : toLang}&dt=t&q=${encodeURIComponent(originalText)}`
                        );
                        
                        if (response.ok) {
                            const result = await response.json();
                            const translatedText = result[0]?.[0]?.[0] || originalText;
                            textNode.textContent = translatedText;
                            await new Promise(resolve => setTimeout(resolve, 100));
                        }
                    } catch (error) {
                        console.error('Translation error for text node:', error);
                    }
                }

                return tempDiv.innerHTML;
            }

            // Plain text translation
            const response = await fetch(
                `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${fromLang === 'krd' ? 'ku' : fromLang}&tl=${toLang === 'krd' ? 'ku' : toLang}&dt=t&q=${encodeURIComponent(text)}`
            );

            if (!response.ok) {
                throw new Error(`Translation API error: ${response.status}`);
            }

            const result = await response.json();
            
            // Combine all translation segments
            let translatedText = '';
            if (Array.isArray(result) && result[0]) {
                translatedText = result[0]
                    .map((segment: any) => segment?.[0] || '')
                    .filter((text: string) => text && text.trim().length > 0)
                    .join(' ')
                    .trim();
            }

            return translatedText || text;
        } catch (error) {
            console.error('Translation failed:', error);
            throw error;
        }
    };

    // Auto-translate handler - translates current language to all other languages
    const handleAutoTranslate = async () => {
        const currentTitle = getMultilingualValue(data.title, activeLang);
        const currentDescription = getMultilingualValue(data.description, activeLang);

        if ((!currentTitle || currentTitle.trim() === '') && (!currentDescription || currentDescription.trim() === '')) {
            Swal.fire({
                title: t('No content to translate'),
                text: t('Please add content in the current language first'),
                icon: 'warning',
            });
            return;
        }

        const confirmed = await Swal.fire({
            title: t('Auto Translate'),
            text: t('This will translate the current content to all other languages. Continue?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('Yes, translate'),
            cancelButtonText: t('Cancel'),
        });

        if (!confirmed.isConfirmed) return;

        setTranslating(true);
        try {
            const otherLanguages = supportedLanguages.filter(lang => lang !== activeLang);
            const updatedTitle = { ...(data.title || {}) };
            const updatedDescription = { ...(data.description || {}) };

            // Translate title and description to each other language
            for (const targetLang of otherLanguages) {
                try {
                    if (currentTitle && currentTitle.trim()) {
                        const translatedTitle = await translateText(
                            currentTitle,
                            activeLang === 'krd' ? 'ku' : activeLang,
                            targetLang === 'krd' ? 'ku' : targetLang
                        );
                        updatedTitle[targetLang] = translatedTitle;
                    }

                    if (currentDescription && currentDescription.trim()) {
                        // Description is HTML from CKEditor, so use HTML translation
                        const translatedDescription = await translateText(
                            currentDescription,
                            activeLang === 'krd' ? 'ku' : activeLang,
                            targetLang === 'krd' ? 'ku' : targetLang,
                            true // isHtml = true
                        );
                        updatedDescription[targetLang] = translatedDescription;
                    }

                    // Add small delay to avoid rate limiting
                    await new Promise(resolve => setTimeout(resolve, 200));
                } catch (error) {
                    console.error(`Translation failed for ${targetLang}:`, error);
                    Swal.fire({
                        title: t('Translation error'),
                        text: t(`Failed to translate to ${languageNames[targetLang] || targetLang}`),
                        icon: 'error',
                        timer: 2000,
                    });
                }
            }

            setData({
                ...data,
                title: updatedTitle,
                description: updatedDescription
            });

            const translatedCount = otherLanguages.filter(lang => 
                updatedTitle[lang] || updatedDescription[lang]
            ).length;

            Swal.fire({
                title: t('Translation completed'),
                text: t('Content has been translated to all other languages') + ` (${translatedCount}/${otherLanguages.length} languages)`,
                icon: 'success',
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (error: any) {
            console.error('Auto-translate failed:', error);
            Swal.fire({
                title: t('Translation failed'),
                text: error.message || t('An error occurred during translation'),
                icon: 'error',
            });
        } finally {
            setTranslating(false);
        }
    };
    const save = async (iparent: any) => {
        try {
            // Prepare multilingual data from form state
            // The form state should already contain all languages as objects
            let titleObj: any = {};
            let descriptionObj: any = {};
            
            // Convert to objects if they're strings (legacy support)
            if (typeof data.title === 'string') {
                titleObj = { [activeLang]: data.title };
            } else if (data.title && typeof data.title === 'object') {
                titleObj = { ...data.title };
            }
            
            if (typeof data.description === 'string') {
                descriptionObj = { [activeLang]: data.description };
            } else if (data.description && typeof data.description === 'object') {
                descriptionObj = { ...data.description };
            }
            
            // Ensure current language values are set from form
            const currentTitleValue = getMultilingualValue(data.title, activeLang);
            const currentDescriptionValue = getMultilingualValue(data.description, activeLang);
            
            if (currentTitleValue !== undefined && currentTitleValue !== null) {
                titleObj[activeLang] = currentTitleValue;
            }
            if (currentDescriptionValue !== undefined && currentDescriptionValue !== null) {
                descriptionObj[activeLang] = currentDescriptionValue;
            }
            
            // If updating, fetch existing data to merge and preserve all languages
            if (data.id) {
                try {
                    const fetchRes = await axios.get(`${config.url}/v1/dashboard/qa`, {
                        params: { parent: create },
                        headers: { Authorization: 'Bearer ' + cookie['token'] }
                    });
                    
                    // Find the current item in the list
                    const items = fetchRes.data?.message || [];
                    const existingItem = items.find((item: any) => 
                        (item._id === data.id || item.id === data.id)
                    );
                    
                    if (existingItem) {
                        // Merge existing multilingual data with current edits
                        const existingTitle = existingItem.title || {};
                        const existingDescription = existingItem.description || {};
                        
                        // Start with existing data, then overlay with current form data
                        const existingTitleObj = typeof existingTitle === 'string' 
                            ? {} 
                            : { ...existingTitle };
                        const existingDescriptionObj = typeof existingDescription === 'string' 
                            ? {} 
                            : { ...existingDescription };
                        
                        // Merge: existing + current form (form takes precedence for current lang)
                        titleObj = { ...existingTitleObj, ...titleObj };
                        descriptionObj = { ...existingDescriptionObj, ...descriptionObj };
                    }
                } catch (fetchError) {
                    console.warn('Could not fetch existing data for merge, using form data only:', fetchError);
                }
            }
            
            const dataToSend: any = {
                parent: create,
                title: titleObj,
                description: descriptionObj
            };
            
            // Add id only if updating
            if (data.id) {
                dataToSend.id = data.id;
            }
            
            console.log('=== SAVING QA DATA ===');
            console.log('Active Language:', activeLang);
            console.log('Form state:', { title: data.title, description: data.description });
            console.log('Final title object (all languages):', titleObj);
            console.log('Final description object (all languages):', descriptionObj);
            console.log('Data to send:', JSON.stringify(dataToSend, null, 2));
            
            var path = data.id 
                ? `${config.url}/v1/dashboard/qa/${data.id}` // Update existing
                : `${config.url}/v1/dashboard/qa`; // Create new
            
            let res = await axios({
                method: data.id ? 'put' : 'post',
                data: dataToSend,
                url: path,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });
            
            console.log('Save response:', res.data);
            setData({
                parent: iparent,
                title: {},
                description: {}
            })
            await load();
            var i = key + 1;
            setKey(i);
            closeModal();
            Swal.fire({
                icon: 'success',
                title: t('Saved successfully'),
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error: any) {
            console.error('Save error:', error);
            Swal.fire({
                icon: 'error',
                title: t('Error saving'),
                text: error.response?.data?.message || error.message || t('Failed to save QA data')
            })
        }
    }
    const load = async () => {
        try {
            var path = `${config.url}/v1/dashboard/qa/${parent}`;

            let res = await axios({
                method: 'get',
                data: data,
                url: path,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });
            setData({
                parent: parent
            })
            setList(res.data);
            var i = key + 1;
            setKey(i);
            closeModal();
        } catch (error) {
            Swal.fire('Error Data')

        }
    }
    const deleted = async (id: any) => {
        try {
            var path = `${config.url}/v1/dashboard/qa/${id}`;
            data.parent = create;
            let res = await axios({
                method: 'delete',
                url: path,
                headers: {
                    Authorization: 'Bearer ' + cookie['token'],
                }
            });

            await load();
            var i = key + 1;
            setKey(i);
        } catch (error) {
            Swal.fire({
                icon: 'warning',
                title: t('Has subcategories')
            })

        }
    }
    const newquiz = (iparent: any, editItem?: any) => {
        SetCreate(iparent);
        // If editing, load the item data; otherwise reset form
        if (editItem) {
            setData({
                parent: iparent,
                title: editItem.title || {},
                description: editItem.description || {},
                id: editItem.id
            });
        } else {
            setData({
                parent: iparent,
                title: {},
                description: {}
            });
        }
        openModal();
    }
    useEffect(() => {
        loadLanguages();
        load();
    }, []);

    // CKEditor configuration
    const editorConfig = React.useMemo(() => ({
        language: 'fa',
        toolbar: {
            items: [
                'undo', 'redo', '|',
                'heading', '|',
                'bold', 'italic', 'underline', 'strikethrough', '|',
                'fontFamily', 'fontSize', 'fontColor', 'fontBackgroundColor', '|',
                'alignment', '|',
                'numberedList', 'bulletedList', '|',
                'link', 'blockQuote', 'insertTable', 'horizontalLine', '|',
                'removeFormat', 'sourceEditing'
            ],
            shouldNotGroupWhenFull: true,
        },
        plugins: [
            Essentials,
            Alignment,
            Bold,
            Italic,
            Underline,
            Strikethrough,
            Heading,
            Paragraph,
            Link,
            List,
            FontSize,
            FontFamily,
            FontColor,
            FontBackgroundColor,
            BlockQuote,
            Code,
            HorizontalLine,
            RemoveFormat,
            SourceEditing,
            Table,
            TableToolbar,
            TableProperties,
            TableCellProperties,
            Undo
        ],
        ui: {
            viewportTopOffset: 0
        }
    }), [activeLang]);

    // Handle editor change
    const handleEditorChange = (_: any, editor: any) => {
        const newHtml = editor.getData();
        setData({
            ...data,
            description: setMultilingualValue(data.description, activeLang, newHtml)
        });
    };

    // Preserve form data when switching languages (if modal is open)
    useEffect(() => {
        // When language changes and modal is open, ensure data structure is preserved
        if (isOpen && data) {
            // Ensure title and description are objects
            if (typeof data.title === 'string') {
                setData((prev: any) => ({
                    ...prev,
                    title: { [activeLang]: prev.title }
                }));
            }
            if (typeof data.description === 'string') {
                setData((prev: any) => ({
                    ...prev,
                    description: { [activeLang]: prev.description }
                }));
            }
            
            // Update editor content when language changes
            if (editorRef.current) {
                const newDescription = getMultilingualValue(data.description, activeLang);
                editorRef.current.setData(newDescription || '');
            }
        }
    }, [activeLang, isOpen]);
    return (
        <div>

            <div>
                {isOpen && (
                    <>
                        {/* Background overlay */}
                        <div
                            className="fixed inset-0 bg-black opacity-50 z-40  "
                            onClick={closeModal}
                        ></div>

                        {/* Dialog content */}
                        <div className="fixed inset-0 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg shadow-lg p-6 w-[80%] max-w-full">

                                <div key={key}>
                                    <div className="w-full">
                                        {/* Language indicator */}
                                        <div className="mb-4 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
                                            {t('Editing in')}: <strong>{activeLang === 'en' ? 'English' : activeLang === 'fa' ? 'فارسی' : activeLang === 'ar' ? 'العربية' : activeLang === 'krd' ? 'Kurdî' : activeLang === 'tr' ? 'Türkçe' : activeLang}</strong>
                                        </div>
                                        
                                        <div className="text-right">
                                            <label>{t('تیتر')} ({activeLang.toUpperCase()})</label>
                                            <input
                                                value={getMultilingualValue(data.title, activeLang)}
                                                dir='rtl'
                                                className={`${inputstyle}     ${error != null && error['name'] != undefined && `bg-red-100`} `}
                                                placeholder={t('تیتر')}
                                                onChange={(e) => setData({ 
                                                    ...data, 
                                                    title: setMultilingualValue(data.title, activeLang, e.target.value)
                                                })}
                                            />
                                        </div>
                                        <div className="text-right mt-4">
                                            <label className="block mb-2">{t('توضیحات')} ({activeLang.toUpperCase()})</label>
                                            <div className="border border-gray-300 rounded-lg overflow-hidden" dir="rtl">
                                                <CKEditor
                                                    key={`editor-${data?.id || 'new'}-${activeLang}`}
                                                    editor={ClassicEditor}
                                                    // @ts-ignore
                                                    config={editorConfig}
                                                    data={getMultilingualValue(data.description, activeLang)}
                                                    onChange={handleEditorChange}
                                                    onReady={(editor: any) => {
                                                        editorRef.current = editor;
                                                        // Ensure editor has the correct data for current language
                                                        const langDescription = getMultilingualValue(data.description, activeLang);
                                                        if (editor.getData() !== langDescription) {
                                                            editor.setData(langDescription || '');
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {/* onClick={(e) => { save(create) }} */}

                                <div className="flex gap-4 flex-wrap items-center">
                                    <button
                                        type="button"
                                        onClick={handleAutoTranslate}
                                        disabled={translating || !getMultilingualValue(data.title, activeLang) && !getMultilingualValue(data.description, activeLang)}
                                        className="px-5 py-2.5 border border-purple-300 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 hover:border-purple-400 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        title={t('Auto translate to all other languages')}
                                    >
                                        {translating ? (
                                            <>
                                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                <span>{t('Translating...')}</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.036 9.5a18.022 18.022 0 019.568-9M6.412 9a17.982 17.982 0 011.564-2.5m0 0A17.982 17.982 0 0112 3c2.5 0 4.847.655 6.888 1.8M6.412 9.5c.676.5 1.404.921 2.176 1.256M12 21a18.022 18.022 0 005.588-9M12 21c-2.5 0-4.847-.655-6.888-1.8m9.776 0a17.982 17.982 0 01-1.564 2.5m0 0A17.982 17.982 0 0112 21m-4.412-1.5c.676-.5 1.404-.921 2.176-1.256" />
                                                </svg>
                                                <span>{t('Auto Translate')}</span>
                                            </>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => { save(create) }}
                                        type="button" className="py-2 mt-5 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-700 dark:focus:bg-neutral-700" data-hs-overlay="#hs-stacked-overlays-2"
                                        data-hs-overlay-options='{
          "isClosePrev": false
        }'>
                                        {t('Save')}
                                    </button>
                                    <button
                                        onClick={() => { closeModal() }}
                                        type="button" className="py-2 mt-5 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-800 shadow-sm hover:bg-gray-50 focus:outline-none focus:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-800 dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-700 dark:focus:bg-neutral-700" data-hs-overlay="#hs-stacked-overlays-2"
                                        data-hs-overlay-options='{
          "isClosePrev": false
        }'>
                                        {t('Close')}
                                    </button>
                                </div>

                                </div>
                            </div>
                        </div>
                    </>
                )}


                <div
                    id="hs-scale-animation-modal"
                    className="hs-overlay hidden overflow-x-hidden size-full fixed top-0 start-0 z-[3888]  overflow-y-auto pointer-events-none"
                    role="dialog"
                    tabIndex={-1}
                    aria-labelledby="hs-scale-animation-modal-label"
                >
                    <div className=" hs-overlay-animation-target hs-overlay-open:scale-100 hs-overlay-open:opacity-100 scale-95 opacity-0 ease-in-out transition-all duration-200 sm:max-w-lg sm:w-full m-3 sm:mx-auto min-h-[calc(100%-3.5rem)] flex items-center">
                        <div className="w-full p-6 flex flex-col bg-white border shadow-sm rounded-xl pointer-events-auto dark:bg-neutral-800 dark:border-neutral-700 dark:shadow-neutral-700/70">

                        </div>
                    </div>
                </div>
                <div className="m-4 flex items-center justify-between gap-4 flex-wrap">
                    {/* Language Switcher */}
                    {!propActiveLang && languages.length > 0 && (
                        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg p-2">
                            <span className="text-sm font-medium text-gray-700">{t('Language')}:</span>
                            <div className="flex gap-1">
                                {languages.map((lang) => (
                                    <button
                                        key={lang.code}
                                        type="button"
                                        onClick={() => setInternalActiveLang(lang.code)}
                                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                                            activeLang === lang.code
                                                ? 'bg-blue-600 text-white shadow-md'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                        }`}
                                    >
                                        {lang.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    
                    <button type="button" className="
                        py-3 px-4 inline-flex  items-center gap-x-2 
                        text-sm font-medium rounded-lg border 
                        border-transparent bg-blue-600 text-white
                        hover:bg-blue-700 focus:outline-none focus:bg-blue-700
                        disabled:opacity-50 disabled:pointer-events-none
                    " onClick={(e) => { newquiz(parent) }}>
                        {t('New Question')}
                    </button>
                </div>


                <div className="hs-accordion-group">
                    {
                        list != null && list.message.map((item: any) => {
                            return (
                                <div dir="rtl" className="text-right">

                                    <div className="flex flex-col bg-white border shadow-sm rounded-xl dark:bg-neutral-900 dark:border-neutral-700 dark:shadow-neutral-700/70">
                                        <div className="text-left">
                                            <button type="button" onClick={(e) => { deleted(item.id) }} className="bg-red-600 p-2 rounded-[20]">
                                                <img src="/icons/trash.svg" width={20}></img>
                                            </button>
                                        </div>

                                        <div className="p-4 md:p-5">
                                            <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                                                {getMultilingualValue(item.title, activeLang) || t('No title')}
                                            </h3>
                                            <div 
                                                className="mt-2 text-gray-500 dark:text-neutral-400 prose prose-sm max-w-none prose-headings:font-semibold prose-p:text-gray-700 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                                                dir="rtl"
                                                dangerouslySetInnerHTML={{ 
                                                    __html: getMultilingualValue(item.description, activeLang) || `<p class="text-gray-400 italic">${t('No description')}</p>` 
                                                }} 
                                            />
                                            <div className="flex gap-2 mt-5">
                                                <button type="button" className="
                                                    py-3 px-4 inline-flex items-center gap-x-2 
                                                    text-sm font-medium rounded-lg border 
                                                    border-transparent bg-green-600 text-white
                                                    hover:bg-green-700 focus:outline-none focus:bg-green-700
                                                    disabled:opacity-50 disabled:pointer-events-none
                                                " onClick={(e) => { newquiz(item.parent || parent, item) }}>
                                                    {t('Edit')}
                                                </button>
                                                <button type="button" className="
                                                    py-3 px-4 inline-flex items-center gap-x-2 
                                                    text-sm font-medium rounded-lg border 
                                                    border-transparent bg-blue-600 text-white
                                                    hover:bg-blue-700 focus:outline-none focus:bg-blue-700
                                                    disabled:opacity-50 disabled:pointer-events-none
                                                " onClick={(e) => { newquiz(item.id) }}>
                                                    {t('New Answer')}
                                                </button>
                                            </div>
                                        </div>
                                        {item.toAnswer.map((i: any) => {
                                            return (
                                                <div className="p-4 md:p-5 bg-red-100 border-[1px] m-4 rounded-[20] ">
                                                    <div className="text-left">
                                                        <button type="button" onClick={(e) => { deleted(i.id) }} className="bg-red-600 p-2 rounded-[20]">
                                                            <img src="/icons/trash.svg" width={20}></img>
                                                        </button>
                                                    </div>
                                                    <h3 className="text-lg font-bold text-gray-800 dark:text-white">
                                                        {getMultilingualValue(i.title, activeLang) || t('No title')}
                                                    </h3>
                                                    <div 
                                                        className="mt-2 text-gray-500 dark:text-neutral-400 prose prose-sm max-w-none prose-headings:font-semibold prose-p:text-gray-700 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                                                        dir="rtl"
                                                        dangerouslySetInnerHTML={{ 
                                                            __html: getMultilingualValue(i.description, activeLang) || `<p class="text-gray-400 italic">${t('No description')}</p>` 
                                                        }} 
                                                    />

                                                </div>
                                            )
                                        })}

                                    </div>

                                </div>
                            )
                        })
                    }

                </div>
            </div>
        </div>
    )
}